'use client'

import React from 'react'
import { Button } from '@/shared/components/ui/button'
import { Camera, FileText, Keyboard, Mic, PlusIcon, TrashIcon, X, Download } from 'lucide-react'
import AddGroupPhotoModal from '@/features/group-photos/components/add-group-photo-modal'
import { updateGroupPhoto, deleteGroupPhoto, type GroupPhoto } from '@/features/group-photos/api/group-photos.api'
import VideoThumbnail from '@/features/photos/components/video-thumbnail'
import { PdfThumbnail } from '@/features/photos/components/pdf-thumbnail'
import VideoPlayer from '@/features/photos/components/video-player'
import PhotoDescriptionIndicator from '@/features/photos/components/photo-description-indicator'
import { ZoomableImage } from '@/features/photos/components/zoomable-image'
import CameraCaptureModal from '@/features/photos/components/camera-capture-modal'
import PostCapturePopup from '@/features/photos/components/post-capture-popup'
import VoiceRecordingModal from '@/features/photos/components/voice-recording-modal'
import TextInputModal from '@/features/photos/components/text-input-modal'
import { downloadPhotoOrVideo } from '@/features/photos/lib/download-media'

const CONFIDENTIAL_TAG = 'Confidential'
const PHOTOS_SHARING_MESSAGE =
  "Thanks for sharing! By posting your photo or video, you're giving ReliefHub the green light to display it across the platform for all users to see and celebrate."

export interface TimelineDay {
  date: string
  displayDate: string
  photos: GroupPhoto[]
}

export interface CapturedPhotoForContent {
  file: File
  url: string
  thumbnailUrl: string | null
  takenAt: string
  tempId: string
}

export interface GroupPhotosTimelineTabContentProps {
  groupId: string
  fileInputRef: React.Ref<HTMLInputElement>
  timelineDays: TimelineDay[]
  photoTagFilter: string | null
  setPhotoTagFilter: (v: string | null) => void
  setIsCameraOpen: (v: boolean) => void
  handleUploadButtonClick: () => void
  handlePhotoClick: (photo: GroupPhoto) => void
  handleFileSelection: (e: React.ChangeEvent<HTMLInputElement>) => void
  isModalOpen: boolean
  setIsModalOpen: (v: boolean) => void
  setPreSelectedFiles: (v: File[]) => void
  handleUploadSuccess: () => void
  preSelectedFiles: File[]
  selectedPhoto: GroupPhoto | null
  setSelectedPhoto: React.Dispatch<React.SetStateAction<GroupPhoto | null>>
  selectedVideo: GroupPhoto | null
  setSelectedVideo: React.Dispatch<React.SetStateAction<GroupPhoto | null>>
  pendingViewerDescription: string | undefined
  setPendingViewerDescription: React.Dispatch<React.SetStateAction<string | undefined>>
  isViewerVoiceOpen: boolean
  setIsViewerVoiceOpen: (v: boolean) => void
  isViewerTextOpen: boolean
  setIsViewerTextOpen: (v: boolean) => void
  isViewerTranscribing: boolean
  handleViewerVoiceSave: (audioBlob: Blob) => Promise<void>
  handleViewerTextSave: (text: string) => Promise<void>
  formatDate: (dateString: string | null) => string
  closePhotoViewer: () => void
  canDownload: boolean
  canViewConfidential: boolean
  canDeletePhoto: (photo: GroupPhoto | null | undefined) => boolean
  canEditDescription: (photo: GroupPhoto | null | undefined) => boolean
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>
  isCameraOpen: boolean
  cleanupAllPhotos: () => Promise<void>
  handleCameraCapture: (file: File) => void
  capturedPhotos: CapturedPhotoForContent[]
  lastSavedThumbnailUrl: string | null
  showPostCapturePopup: boolean
  isVoiceRecordingOpen: boolean
  setIsVoiceRecordingOpen: (v: boolean) => void
  isTextInputOpen: boolean
  setIsTextInputOpen: (v: boolean) => void
  currentPhotoTempId: string | null
  handlePostCaptureVoiceRecord: (takenAt: string) => void
  handlePostCaptureTextInput: (takenAt: string) => void
  handlePostCaptureClose: (takenAt: string) => void
  onPostCaptureTakenAtChange: (tempId: string, takenAt: string) => void
  handleVoiceRecordingSave: (audioBlob: Blob) => Promise<void>
  handleTextInputSave: (text: string) => Promise<void>
  isTextInputSavingRef: React.MutableRefObject<boolean>
}

export function GroupPhotosTimelineTabContent(props: GroupPhotosTimelineTabContentProps) {
  const {
    groupId,
    fileInputRef,
    timelineDays,
    photoTagFilter,
    setPhotoTagFilter,
    setIsCameraOpen,
    handleUploadButtonClick,
    handlePhotoClick,
    handleFileSelection,
    isModalOpen,
    setIsModalOpen,
    setPreSelectedFiles,
    handleUploadSuccess,
    preSelectedFiles,
    selectedPhoto,
    setSelectedPhoto,
    selectedVideo,
    setSelectedVideo,
    pendingViewerDescription,
    setPendingViewerDescription,
    isViewerVoiceOpen,
    setIsViewerVoiceOpen,
    isViewerTextOpen,
    setIsViewerTextOpen,
    isViewerTranscribing,
    handleViewerVoiceSave,
    handleViewerTextSave,
    formatDate,
    closePhotoViewer,
    canDownload,
    canViewConfidential,
    canDeletePhoto,
    canEditDescription,
    setRefreshKey,
    isCameraOpen,
    cleanupAllPhotos,
    handleCameraCapture,
    capturedPhotos,
    lastSavedThumbnailUrl,
    showPostCapturePopup,
    isVoiceRecordingOpen,
    setIsVoiceRecordingOpen,
    isTextInputOpen,
    setIsTextInputOpen,
    currentPhotoTempId,
    handlePostCaptureVoiceRecord,
    handlePostCaptureTextInput,
    handlePostCaptureClose,
    onPostCaptureTakenAtChange,
    handleVoiceRecordingSave,
    handleTextInputSave,
    isTextInputSavingRef,
  } = props

  return (
    <>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">Filter:</span>
            <Button
              type="button"
              variant={photoTagFilter === null ? 'default' : 'outline'}
              size="sm"
              onClick={() => setPhotoTagFilter(null)}
            >
              All
            </Button>
            {canViewConfidential && (
              <Button
                type="button"
                variant={photoTagFilter === CONFIDENTIAL_TAG ? 'default' : 'outline'}
                size="sm"
                onClick={() => setPhotoTagFilter(photoTagFilter === CONFIDENTIAL_TAG ? null : CONFIDENTIAL_TAG)}
              >
                Confidential {photoTagFilter === CONFIDENTIAL_TAG ? 'On' : 'Off'}
              </Button>
            )}
          </div>
          <div className="flex items-center gap-2 sm:justify-end">
            <Button onClick={() => setIsCameraOpen(true)} className="gap-2 bg-primary hover:bg-primary/90 text-white" size="sm">
              <Camera className="w-3 h-3" />
              Take
            </Button>
            <Button onClick={handleUploadButtonClick} size="sm" className="gap-2">
              <PlusIcon className="w-3 h-3" />
              Upload
            </Button>
          </div>
        </div>

        <div>
          {timelineDays.length > 0 ? (
            <div className="space-y-8">
              {timelineDays.map((day) => (
                <div key={day.date} className="space-y-4">
                  <div className="flex items-center gap-4">
                    <h3 className="text-sm md:text-lg font-semibold text-foreground">{day.displayDate}</h3>
                    <div className="flex-1 h-px bg-border" />
                    <span className="text-sm text-muted-foreground">
                      {day.photos.length} {day.photos.length !== 1 ? 'items' : 'item'}
                    </span>
                  </div>
                  <div className="min-w-0">
                    {day.photos.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {day.photos.map((photo) => {
                          const isVideo = photo.mediaType === 'video'
                          const isDocument = photo.mediaType === 'document'
                          return (
                            <div
                              key={photo.id}
                              className="relative group bg-card rounded border overflow-hidden cursor-pointer hover:shadow-md transition-shadow inline-block"
                              onClick={() => handlePhotoClick(photo)}
                            >
                              {isVideo ? (
                                <VideoThumbnail
                                  photo={photo as any}
                                  onClick={() => handlePhotoClick(photo)}
                                  showDuration
                                  showVideoIcon
                                />
                              ) : isDocument ? (
                                <>
                                  <PdfThumbnail
                                    downloadApiPath={`/api/group-photos/${photo.id}/download`}
                                    className="object-cover rounded"
                                    style={{ height: '200px', width: 'auto' }}
                                    alt={photo.description || `PDF ${photo.id}`}
                                  >
                                    <div className="flex flex-col items-center justify-center rounded p-4 h-[200px] min-w-[140px] bg-muted">
                                      <FileText className="w-12 h-12 text-muted-foreground mb-2" />
                                      <span className="text-xs text-center text-muted-foreground">PDF</span>
                                    </div>
                                  </PdfThumbnail>
                                  <PhotoDescriptionIndicator hasDescription={!!photo.description} />
                                </>
                              ) : (
                                <>
                                  {/* eslint-disable-next-line @next/next/no-img-element */}
                                  <img
                                    src={photo.thumbnailUrl || photo.imageUrl}
                                    alt={photo.description || `Photo ${photo.id}`}
                                    className="object-cover rounded"
                                    style={{ height: '200px', width: 'auto' }}
                                  />
                                  <PhotoDescriptionIndicator hasDescription={!!photo.description} />
                                </>
                              )}
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="text-sm text-muted-foreground py-4">No photos on this date</div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <p>No photos yet. Click &quot;Take&quot; or &quot;Upload&quot; to add photos, videos, or PDFs.</p>
            </div>
          )}
        </div>
        <p className="text-xs text-muted-foreground rounded-md border border-border bg-muted/50 px-3 py-2 text-center">
          {PHOTOS_SHARING_MESSAGE}
        </p>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*,video/*,.pdf,application/pdf"
        multiple
        onChange={handleFileSelection}
        className="hidden"
      />

      <AddGroupPhotoModal
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false)
          setPreSelectedFiles([])
        }}
        groupId={groupId}
        onUploadSuccess={handleUploadSuccess}
        initialFiles={preSelectedFiles}
      />

      {selectedPhoto && selectedPhoto.mediaType !== 'video' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
          <div className="relative w-[700px] h-[80vh] mx-4">
            <div className="relative w-full h-full bg-white rounded-lg overflow-hidden flex flex-col">
              <div className="flex-1 relative min-h-0">
                {selectedPhoto.mediaType === 'document' ? (
                  <>
                    <iframe
                      src={selectedPhoto.imageUrl}
                      title={selectedPhoto.description || `Document ${selectedPhoto.id}`}
                      className="w-full h-full rounded"
                    />
                    <a
                      href={selectedPhoto.imageUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="absolute bottom-2 left-2 text-xs text-primary underline"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open PDF in new tab
                    </a>
                  </>
                ) : (
                  <ZoomableImage
                    src={selectedPhoto.imageUrl}
                    alt={selectedPhoto.description || `Photo ${selectedPhoto.id}`}
                    className="w-full h-full"
                    showControls
                  />
                )}
                <div className="absolute bottom-2 right-2">
                  <PhotoDescriptionIndicator
                    hasDescription={
                      ((pendingViewerDescription !== undefined ? pendingViewerDescription : selectedPhoto?.description)?.trim()
                        .length ?? 0) > 0
                    }
                  />
                </div>
              </div>
              <div className="bg-white text-gray-800 p-3 border-t flex-shrink-0">
                {isViewerTranscribing ? (
                  <div className="mb-3 pb-3 border-b">
                    <p className="text-sm text-muted-foreground">Transcribing...</p>
                  </div>
                ) : (
                  (() => {
                    const displayDescription =
                      pendingViewerDescription !== undefined
                        ? pendingViewerDescription
                        : selectedPhoto.description
                    return displayDescription ? (
                      <div className="mb-3 pb-3 border-b">
                        <p className="text-sm text-gray-800 whitespace-pre-wrap">{displayDescription}</p>
                      </div>
                    ) : null
                  })()
                )}
                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                  {canEditDescription(selectedPhoto) ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setIsViewerVoiceOpen(true)}>
                        <Mic className="w-4 h-4" />
                        Voice
                      </Button>
                      <Button type="button" variant="outline" size="sm" className="gap-1.5" onClick={() => setIsViewerTextOpen(true)}>
                        <Keyboard className="w-4 h-4" />
                        Keyboard
                      </Button>
                    </div>
                  ) : null}
                  {canViewConfidential && (
                    <Button
                      type="button"
                      variant={(selectedPhoto.tags ?? []).includes(CONFIDENTIAL_TAG) ? 'default' : 'outline'}
                      size="sm"
                      className="gap-1.5 shrink-0"
                      onClick={async () => {
                        const current = selectedPhoto.tags ?? []
                        const hasTag = current.includes(CONFIDENTIAL_TAG)
                        const nextTags = hasTag ? current.filter((t) => t !== CONFIDENTIAL_TAG) : [...current, CONFIDENTIAL_TAG]
                        try {
                          const result = await updateGroupPhoto(selectedPhoto.id, { tags: nextTags })
                          if (result.success) {
                            setSelectedPhoto((prev) => (prev ? { ...prev, tags: nextTags } : null))
                            setRefreshKey((prev) => prev + 1)
                          } else {
                            alert(`Failed to update tag: ${result.message ?? 'Unknown error'}`)
                          }
                        } catch {
                          alert('Failed to update tag')
                        }
                      }}
                      aria-label={((selectedPhoto.tags ?? []).includes(CONFIDENTIAL_TAG) ? 'Remove' : 'Add') + ' Confidential tag'}
                    >
                      Confidential {(selectedPhoto.tags ?? []).includes(CONFIDENTIAL_TAG) ? 'On' : 'Off'}
                    </Button>
                  )}
                </div>
                <div className="flex justify-between items-center gap-2 flex-wrap">
                  <p className="text-xs text-gray-500">
                    {selectedPhoto.uploadedByUser?.fullname && (
                      <span className="block mb-0.5">
                        Added by {selectedPhoto.uploadedByUser.fullname}
                      </span>
                    )}
                    {formatDate(selectedPhoto.takenAt || selectedPhoto.uploadedAt)}
                  </p>
                  <div className="flex items-center gap-3">
                    {canDownload && (
                      <Button
                        variant="default"
                        size="icon"
                        className="h-8 w-8"
                        onClick={() =>
                          downloadPhotoOrVideo(selectedPhoto.imageUrl, {
                            id: selectedPhoto.id,
                            mediaType: (selectedPhoto.mediaType as 'image' | 'video' | 'document') ?? 'image',
                            downloadApiPath: `/api/group-photos/${selectedPhoto.id}/download`,
                          })
                        }
                        aria-label="Download"
                      >
                        <Download className="w-4 h-4" />
                      </Button>
                    )}
                    {canDeletePhoto(selectedPhoto) && (
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-8 w-8"
                        onClick={async () => {
                          if (!confirm('Are you sure you want to delete this photo?')) return
                          try {
                            const result = await deleteGroupPhoto(selectedPhoto.id)
                            if (result.success) {
                              closePhotoViewer()
                              setRefreshKey((prev) => prev + 1)
                            } else {
                              alert(`Failed to delete: ${result.message ?? 'Unknown error'}`)
                            }
                          } catch {
                            alert('An error occurred while deleting')
                          }
                        }}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8"
                      onClick={closePhotoViewer}
                      aria-label="Close"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedPhoto && isViewerVoiceOpen && (
        <VoiceRecordingModal
          isOpen={isViewerVoiceOpen}
          onClose={() => setIsViewerVoiceOpen(false)}
          onSave={handleViewerVoiceSave}
          autoStart
          imageUrl={selectedPhoto.imageUrl}
        />
      )}

      {selectedPhoto && isViewerTextOpen && (
        <TextInputModal
          isOpen={isViewerTextOpen}
          onClose={() => setIsViewerTextOpen(false)}
          onSave={handleViewerTextSave}
          initialValue={
            pendingViewerDescription !== undefined ? pendingViewerDescription : selectedPhoto.description ?? ''
          }
          imageUrl={selectedPhoto.imageUrl}
        />
      )}

      {selectedVideo && selectedVideo.mediaType === 'video' && (
        <VideoPlayer
          photo={selectedVideo as any}
          isOpen
          onClose={() => setSelectedVideo(null)}
          onDelete={() => setRefreshKey((prev) => prev + 1)}
          canDelete={canDeletePhoto(selectedVideo)}
          canDownload={canDownload}
          downloadApiPath={`/api/group-photos/${selectedVideo.id}/download`}
          deletePhotoFn={async (id) => {
            const res = await deleteGroupPhoto(id)
            return {
              success: res.success,
              message: res.success ? undefined : res.message,
            }
          }}
        />
      )}

      <CameraCaptureModal
        isOpen={isCameraOpen}
        onClose={async () => {
          await cleanupAllPhotos()
          setIsCameraOpen(false)
        }}
        onCapture={handleCameraCapture}
        lastCapturedThumbnail={
          capturedPhotos.length > 0 ? capturedPhotos[capturedPhotos.length - 1].thumbnailUrl : lastSavedThumbnailUrl
        }
        hideDone={showPostCapturePopup || isVoiceRecordingOpen || isTextInputOpen}
      />

      {showPostCapturePopup && currentPhotoTempId !== null &&
        (() => {
          const currentPhoto = capturedPhotos.find((p) => p.tempId === currentPhotoTempId)
          return currentPhoto ? (
            <PostCapturePopup
              imageUrl={currentPhoto.url}
              initialTakenAt={currentPhoto.takenAt}
              onTakenAtChange={(takenAt) => onPostCaptureTakenAtChange(currentPhotoTempId, takenAt)}
              onVoiceRecord={handlePostCaptureVoiceRecord}
              onTextInput={handlePostCaptureTextInput}
              onClose={handlePostCaptureClose}
            />
          ) : null
        })()}

      {isVoiceRecordingOpen && currentPhotoTempId !== null &&
        (() => {
          const currentPhoto = capturedPhotos.find((p) => p.tempId === currentPhotoTempId)
          return currentPhoto ? (
            <VoiceRecordingModal
              isOpen={isVoiceRecordingOpen}
              onClose={() => {
                setIsVoiceRecordingOpen(false)
                if (currentPhotoTempId !== null) {
                  const p = capturedPhotos.find((x) => x.tempId === currentPhotoTempId)
                  handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
                }
              }}
              onSave={handleVoiceRecordingSave}
              autoStart
              imageUrl={currentPhoto.url}
            />
          ) : null
        })()}

      {isTextInputOpen && currentPhotoTempId !== null &&
        (() => {
          const currentPhoto = capturedPhotos.find((p) => p.tempId === currentPhotoTempId)
          return currentPhoto ? (
            <TextInputModal
              isOpen={isTextInputOpen}
              onClose={() => {
                if (!isTextInputSavingRef.current) {
                  setIsTextInputOpen(false)
                  if (currentPhotoTempId !== null) {
                    const p = capturedPhotos.find((x) => x.tempId === currentPhotoTempId)
                    handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
                  }
                }
              }}
              onSave={handleTextInputSave}
              onSavingChange={(saving) => {
                isTextInputSavingRef.current = saving
              }}
              imageUrl={currentPhoto.url}
            />
          ) : null
        })()}
    </>
  )
}
